import { getAuth } from 'firebase/auth';
import React from 'react';

import { api } from '@/utils/api';

import { useExtensionContext } from '~/components/extension.context';

export const useLoginExtension = () => {
  const [{ extensionId, extensionConnectedStatus }, { postMessage }] = useExtensionContext();
  const generateTokenMutation = api.auth.token2.useMutation();
  const mutateAsync = generateTokenMutation.mutateAsync;

  // Send auth to extension via chrome.runtime port (existing flow)
  React.useEffect(() => {
    if (extensionId && extensionConnectedStatus === 'connected') {
      mutateAsync({
        userAgent: window.navigator.userAgent,
      })
        .then((token) => postMessage({ type: 'LOGIN', data: { token } }))
        .catch((error) => {
          console.error(error);
        });
    }
  }, [mutateAsync, extensionId, extensionConnectedStatus, postMessage]);

  // Also send auth via window.postMessage for content script (side panel support)
  // This runs when the extension is detected on the page
  React.useEffect(() => {
    if (!extensionId) return;

    const sendAuthToContentScript = async () => {
      try {
        const auth = getAuth();
        const user = auth.currentUser;
        if (!user) return;

        // Get fresh token
        const idToken = await user.getIdToken(true);

        // Get token result for expiration info
        const tokenResult = await user.getIdTokenResult();
        const expirationTime = new Date(tokenResult.expirationTime).getTime();
        const expiresInSeconds = Math.floor((expirationTime - Date.now()) / 1000);

        // Note: Firebase client SDK doesn't expose refreshToken directly
        // We need to get it from the internal auth state
        const refreshToken = (user as unknown as { stsTokenManager?: { refreshToken?: string } })
          .stsTokenManager?.refreshToken;

        if (!refreshToken) {
          console.warn('[useLoginExtension] Could not get refresh token');
          return;
        }

        // Send auth data to content script via window.postMessage
        window.postMessage(
          {
            type: 'LOGIN_DIRECT',
            data: {
              idToken,
              refreshToken,
              userId: user.uid,
              email: user.email ?? '',
              expiresInSeconds,
            },
          },
          '*',
        );
      } catch (error) {
        console.error('[useLoginExtension] Failed to send auth to content script:', error);
      }
    };

    // Small delay to ensure content script is ready
    const timeout = setTimeout(() => {
      void sendAuthToContentScript();
    }, 500);
    return () => clearTimeout(timeout);
  }, [extensionId]);
};
